// src/api/staticDataService.js
import { getCurrentPricesFromCoinGecko, getHistoricalDataFromCoinGecko } from './providers/coinGeckoProvider';
import { getCurrentPricesFromCoinDesk, getHistoricalDataFromCoinDesk } from './providers/coinDeskProvider';

class StaticDataService {
  constructor() {
    // Statischer Datenspeicher
    this.data = {
      currentPrices: {},
      historicalData: {
        '1h': {},
        '24h': {},
        '30d': {}
      }
    };
    
    // Zeitstempel der letzten Aktualisierung
    this.lastUpdate = {
      currentPrices: 0,
      historicalData: {
        '1h': 0,
        '24h': 0,
        '30d': 0
      }
    };
    
    // Unterstützte Coins
    this.supportedCoins = ['bitcoin', 'ethereum', 'solana', 'ripple'];
    
    // Flag, ob die erste Datenabrufwelle erfolgt ist
    this.initialFetchDone = false;
    
    // API-Abrufintervalle (in Millisekunden)
    this.fetchIntervals = {
      currentPrices: 5 * 60 * 1000,   // Alle 5 Minuten
      '1h': 1 * 60 * 1000,           // Alle 1 Minute 
      '24h': 10 * 60 * 1000,         // Alle 10 Minuten
      '30d': 60 * 60 * 1000          // Alle 60 Minuten
    };
    
    // API-Abrufzähler für Monitoring
    this.apiCallCount = {
      total: 0,
      coinGecko: 0,
      coinDesk: 0
    };
  }
  
  // Initialisierung und Starten der Abrufintervalle
  initialize() {
    // Initial alle Daten abrufen
    this.fetchAllData();
    
    // Zeitgesteuerte Abrufe einrichten
    setInterval(() => this.fetchCurrentPrices(), this.fetchIntervals.currentPrices);
    setInterval(() => this.fetchHistoricalData('1h'), this.fetchIntervals['1h']);
    setInterval(() => this.fetchHistoricalData('24h'), this.fetchIntervals['24h']);
    setInterval(() => this.fetchHistoricalData('30d'), this.fetchIntervals['30d']);
    
    console.log('Statischer Datendienst initialisiert.');
  }
  
  // Alle Daten bei Systemstart abrufen
  async fetchAllData() {
    try {
      await Promise.all([
        this.fetchCurrentPrices(),
        this.fetchHistoricalData('1h'),
        this.fetchHistoricalData('24h'),
        this.fetchHistoricalData('30d')
      ]);
      
      this.initialFetchDone = true;
      console.log('Initiale Datenabrufe abgeschlossen.');
    } catch (error) {
      console.error('Fehler beim initialen Datenabruf:', error);
    }
  }
  
  // Dummy-Daten für Tests generieren
  generateDummyData(timeRange) {
    console.log(`Generiere Dummy-Daten für ${timeRange}...`);
    
    const now = Math.floor(Date.now() / 1000);
    let dataPoints, interval;
    
    switch(timeRange) {
      case '1h':
        dataPoints = 60; // 60 Minuten
        interval = 60; // 1 Minute in Sekunden
        break;
      case '24h':
        dataPoints = 24; // 24 Stunden
        interval = 3600; // 1 Stunde in Sekunden
        break;
      case '30d':
        dataPoints = 30; // 30 Tage
        interval = 86400; // 1 Tag in Sekunden
        break;
      default:
        dataPoints = 30;
        interval = 86400;
    }
    
    this.supportedCoins.forEach(coin => {
      const basePrice = coin === 'bitcoin' ? 60000 : 
                        coin === 'ethereum' ? 3000 : 
                        coin === 'solana' ? 150 : 50;
      
      const data = [];
      let lastClose = basePrice;
      
      for (let i = dataPoints - 1; i >= 0; i--) {
        const timestamp = now - (i * interval);
        const volatility = 0.01; // 1% Volatilität
        const changePercent = (Math.random() - 0.5) * 2 * volatility;
        const open = lastClose;
        const close = open * (1 + changePercent);
        const high = Math.max(open, close) * (1 + Math.random() * volatility);
        const low = Math.min(open, close) * (1 - Math.random() * volatility);
        const volume = basePrice * 10 * (Math.random() + 0.5);
        
        data.push({
          timestamp,
          open,
          high,
          low,
          close,
          volume
        });
        
        lastClose = close;
      }
      
      this.data.historicalData[timeRange][coin] = data;
    });
    
    this.lastUpdate.historicalData[timeRange] = Date.now();
  }
  
  // Aktuelle Preise abrufen
  async fetchCurrentPrices() {
    // Dummy-Preise für Tests
    const dummyPrices = {
      bitcoin: {
        usd: 60000 + (Math.random() - 0.5) * 1000,
        usd_24h_change: (Math.random() - 0.5) * 5,
        last_updated_at: Math.floor(Date.now() / 1000)
      },
      ethereum: {
        usd: 3000 + (Math.random() - 0.5) * 100,
        usd_24h_change: (Math.random() - 0.5) * 5,
        last_updated_at: Math.floor(Date.now() / 1000)
      },
      solana: {
        usd: 150 + (Math.random() - 0.5) * 10,
        usd_24h_change: (Math.random() - 0.5) * 8,
        last_updated_at: Math.floor(Date.now() / 1000)
      },
      ripple: {
        usd: 0.50 + (Math.random() - 0.5) * 0.05,
        usd_24h_change: (Math.random() - 0.5) * 6,
        last_updated_at: Math.floor(Date.now() / 1000)
      }
    };

    this.data.currentPrices = dummyPrices;
    this.lastUpdate.currentPrices = Date.now();
    
    // API-Logik auskommentieren für Tests
    /*
    try {
      console.log('Rufe aktuelle Preise ab...');
      
      // Versuche zuerst CoinGecko
      try {
        const data = await getCurrentPricesFromCoinGecko(this.supportedCoins);
        this.data.currentPrices = data;
        this.lastUpdate.currentPrices = Date.now();
        this.apiCallCount.total++;
        this.apiCallCount.coinGecko++;
        console.log('Aktuelle Preise von CoinGecko aktualisiert');
      } catch (error) {
        // Fallback zu CoinDesk
        console.warn('Fehler bei CoinGecko, verwende CoinDesk:', error.message);
        const data = await getCurrentPricesFromCoinDesk(this.supportedCoins);
        this.data.currentPrices = data;
        this.lastUpdate.currentPrices = Date.now();
        this.apiCallCount.total++;
        this.apiCallCount.coinDesk++;
        console.log('Aktuelle Preise von CoinDesk aktualisiert');
      }
    } catch (error) {
      console.error('Fehler beim Abrufen aktueller Preise:', error);
    }
    */
  }
  
  // Historische Daten abrufen
  async fetchHistoricalData(timeRange) {
    // Für Testzwecke nur Dummy-Daten generieren
    this.generateDummyData(timeRange);
    
    // API-Logik auskommentieren für Tests
    /*
    try {
      console.log(`Rufe Historische Daten für ${timeRange} ab...`);
      
      // Parameter je nach Zeitraum
      let days, interval;
      switch(timeRange) {
        case '1h':
          days = 0.042;
          interval = 'minutely';
          break;
        case '24h':
          days = 1;
          interval = 'hourly';
          break;
        case '30d':
          days = 30;
          interval = 'daily';
          break;
      }
      
      // Für jeden unterstützten Coin
      for (const coin of this.supportedCoins) {
        try {
          // Versuche zuerst CoinGecko
          const data = await getHistoricalDataFromCoinGecko(coin, days, interval);
          if (!this.data.historicalData[timeRange][coin]) {
            this.data.historicalData[timeRange][coin] = [];
          }
          this.data.historicalData[timeRange][coin] = data;
          this.apiCallCount.total++;
          this.apiCallCount.coinGecko++;
          console.log(`${timeRange} Daten für ${coin} von CoinGecko aktualisiert`);
        } catch (error) {
          // Fallback zu CoinDesk
          console.warn(`Fehler bei CoinGecko für ${coin}, verwende CoinDesk:`, error.message);
          try {
            const data = await getHistoricalDataFromCoinDesk(coin, days, interval);
            if (!this.data.historicalData[timeRange][coin]) {
              this.data.historicalData[timeRange][coin] = [];
            }
            this.data.historicalData[timeRange][coin] = data;
            this.apiCallCount.total++;
            this.apiCallCount.coinDesk++;
            console.log(`${timeRange} Daten für ${coin} von CoinDesk aktualisiert`);
          } catch (fallbackError) {
            console.error(`Auch CoinDesk fehlgeschlagen für ${coin}:`, fallbackError.message);
          }
        }
      }
      
      this.lastUpdate.historicalData[timeRange] = Date.now();
    } catch (error) {
      console.error(`Fehler beim Abrufen historischer ${timeRange} Daten:`, error);
    }
    */
  }
  
  // Aktuelle Preisdaten abrufen
  getCurrentPrices() {
    return {
      data: this.data.currentPrices,
      lastUpdate: this.lastUpdate.currentPrices
    };
  }
  
  // Historische Daten für einen bestimmten Zeitraum abrufen
  getHistoricalData(timeRange) {
    return {
      data: this.data.historicalData[timeRange],
      lastUpdate: this.lastUpdate.historicalData[timeRange]
    };
  }
  
  // API-Nutzungsstatistiken abrufen
  getApiUsageStats() {
    return {
      ...this.apiCallCount,
      lastUpdated: {
        currentPrices: new Date(this.lastUpdate.currentPrices).toLocaleString(),
        '1h': new Date(this.lastUpdate.historicalData['1h']).toLocaleString(),
        '24h': new Date(this.lastUpdate.historicalData['24h']).toLocaleString(),
        '30d': new Date(this.lastUpdate.historicalData['30d']).toLocaleString(),
      }
    };
  }
  
  // Prüfen, ob initiale Daten geladen wurden
  isDataReady() {
    return this.initialFetchDone;
  }
}

// Singleton-Instanz
const staticDataService = new StaticDataService();

// Service beim Import initialisieren
staticDataService.initialize();

export default staticDataService;
